import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import Select from 'react-select';
import moment from 'moment';
import { useDebounce } from 'use-debounce';
import { useLocation } from 'react-router-dom';
import { Table } from '../../common/table/Table';
import Pagination from '../../common/table/Pagination';
import MobilePagination from '../../common/table/MobilePagination';
import Search from '../../common/Search';
import DeviceLoginHistoryModal from '../../modals/DeviceLoginHistoryModal';
import { fetchDeviceLogDistricts, fetchDeviceLogs } from '../../../apis/deviceLogsApi';
import { IDeviceLog, IDeviceLogDistrict } from '../../../interface/DeviceLog';

const ITEMS_PER_PAGE = 25;
const DEFAULT_RANGE_DAYS = 30;
const DATE_FORMAT = 'YYYY-MM-DD';

const SITE_TYPE_OPTIONS = [
    { value: 'Before', label: 'Before' },
    { value: 'During', label: 'During' },
    { value: 'After', label: 'After' },
];

// Match the search box: 32px high, navy border, 8px radius, 14px text
const FIELD_HEIGHT = 32;
const NAVY = '#023047';

const selectStyles = {
    control: (base: any, state: any) => ({
        ...base,
        minHeight: FIELD_HEIGHT,
        height: FIELD_HEIGHT,
        borderRadius: 8,
        borderColor: NAVY,
        boxShadow: state.isFocused ? `0 0 0 1px ${NAVY}` : 'none',
        fontSize: 14,
        '&:hover': { borderColor: NAVY },
    }),
    valueContainer: (base: any) => ({ ...base, height: FIELD_HEIGHT - 2, padding: '0 10px' }),
    input: (base: any) => ({ ...base, margin: 0, padding: 0 }),
    placeholder: (base: any) => ({ ...base, color: NAVY }),
    singleValue: (base: any) => ({ ...base, color: NAVY }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: (base: any) => ({ ...base, height: FIELD_HEIGHT - 2 }),
    dropdownIndicator: (base: any) => ({ ...base, color: NAVY, padding: '0 8px' }),
    clearIndicator: (base: any) => ({ ...base, padding: '0 4px' }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
    menu: (base: any) => ({ ...base, width: 'max-content', minWidth: '100%', fontSize: 14 }),
};

type Option = { value: string; label: string };

const formatTime = (value: string | null | undefined) =>
    // Stored in UTC, show it in the viewer's local time
    value ? moment.utc(value).local().format('MM-DD-YYYY hh:mm A') : '';

const DeviceLogs: React.FC = () => {
    const [rows, setRows] = useState<IDeviceLog[]>([]);
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch] = useDebounce(searchQuery, 500);
    const [district, setDistrict] = useState<Option | null>(null);
    const [siteType, setSiteType] = useState<Option | null>(null);
    const [districtOptions, setDistrictOptions] = useState<Option[]>([]);

    // Last 30 days by default (today included), or just today when opened from the dashboard card
    const location = useLocation();
    const openedForToday = (location.state as { today?: boolean } | null)?.today === true;
    const today = moment().format(DATE_FORMAT);
    const [fromDate, setFromDate] = useState(
        openedForToday ? today : moment().subtract(DEFAULT_RANGE_DAYS - 1, 'days').format(DATE_FORMAT)
    );
    const [toDate, setToDate] = useState(today);

    const totalPages = Math.max(Math.ceil(totalItems / ITEMS_PER_PAGE), 1);

    // Load the district list once
    useEffect(() => {
        fetchDeviceLogDistricts()
            .then((list: IDeviceLogDistrict[]) =>
                setDistrictOptions(list.map(d => ({
                    value: d.distNumber,
                    label: d.distName ? `${d.distName} (${d.distNumber})` : d.distNumber,
                })))
            )
            .catch(() => setDistrictOptions([]));
    }, []);

    // Row whose login history popup is open
    const [selectedLog, setSelectedLog] = useState<IDeviceLog | null>(null);
    const closeHistory = useCallback(() => setSelectedLog(null), []);

    // Id of the latest request, so an older response can't overwrite a newer one
    const latestRequest = useRef(0);

    const load = useCallback(async () => {
        const requestId = ++latestRequest.current;
        setIsLoading(true);
        setError(null);
        try {
            const result = await fetchDeviceLogs(
                {
                    search: debouncedSearch.trim(),
                    distNumber: district?.value ?? null,
                    siteType: siteType?.value ?? null,
                    fromDate,
                    toDate,
                },
                currentPage,
                ITEMS_PER_PAGE
            );
            if (requestId !== latestRequest.current) return;
            setRows(result.data);
            setTotalItems(result.totalItems);
        } catch (err) {
            if (requestId !== latestRequest.current) return;
            setError(err instanceof Error ? err.message : 'Failed to load device logs');
            setRows([]);
            setTotalItems(0);
        } finally {
            if (requestId === latestRequest.current) setIsLoading(false);
        }
    }, [debouncedSearch, district, siteType, fromDate, toDate, currentPage]);

    useEffect(() => {
        load();
    }, [load]);

    // Filters always go back to page 1, in the same update so only one request is sent
    const updateSearch = (value: React.SetStateAction<string>) => {
        setSearchQuery(value);
        setCurrentPage(1);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        updateSearch(e.target.value);
    };

    const handleDistrictChange = (option: Option | null) => {
        setDistrict(option);
        setCurrentPage(1);
    };

    const handleSiteTypeChange = (option: Option | null) => {
        setSiteType(option);
        setCurrentPage(1);
    };

    // Dates can't be cleared, so the range always stays set
    const handleFromDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.value) return;
        setFromDate(e.target.value);
        setCurrentPage(1);
    };

    const handleToDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.value) return;
        setToDate(e.target.value);
        setCurrentPage(1);
    };

    const columns: ColumnDef<IDeviceLog>[] = [
        {
            accessorKey: 'personalId',
            header: 'Personnel ID',
            cell: ({ row }) => row.original.personalId || '-',
        },
        {
            accessorKey: 'userName',
            header: 'User Name',
            cell: ({ row }) => row.original.userName || '-',
        },
        {
            accessorKey: 'email',
            header: 'Email',
            cell: ({ row }) => row.original.email || '-',
        },
        {
            accessorKey: 'platform',
            header: 'Platform',
            cell: ({ row }) => row.original.platform || '-',
        },
        {
            accessorKey: 'deviceName',
            header: 'Device Name',
            cell: ({ row }) => row.original.deviceName || '-',
        },
        {
            accessorKey: 'deviceUuid',
            header: 'Device ID',
            cell: ({ row }) => {
                const uuid = row.original.deviceUuid;
                // Long ids are cut with "..."; the full id is in the tooltip and the popup
                return uuid ? <span className="deviceLogUuid" title={uuid}>{uuid}</span> : '-';
            },
        },
        {
            accessorKey: 'createdAt',
            header: 'Last Active',
            cell: ({ row }) => formatTime(row.original.createdAt),
        },
        {
            accessorKey: 'loginCount',
            header: 'History',
            cell: ({ row }) => {
                const log = row.original;
                const count = log.loginCount || 1;
                return (
                    <button
                        type="button"
                        className="deviceLogCount"
                        title="Show every login from this device"
                        onClick={() => setSelectedLog(log)}
                    >
                        {count}
                    </button>
                );
            },
        },
    ];

    return (
        <>
            <div className="seachStrip pb-lg-4 pb-md-3 pb-3">
                <div className="row g-4 flex-lg-row flex-md-row flex-sm-row flex-column">
                    <div className="col-12 d-flex align-items-center justify-content-between gap-3">
                        <div className="DistrictpageSubTitle">
                            <h2 className="d-flex m-0 align-items-center text-nowrap gap-3">
                                <b>Device Logs</b>
                            </h2>
                        </div>
                    </div>

                    <div className="col-12 d-flex align-items-center justify-content-end gap-2">
                        <div className="d-flex w-100 flex-lg-nowrap flex-md-wrap flex-wrap align-items-end justify-content-between gap-3">
                            <div className="deviceLogsFilters d-flex flex-wrap align-items-center gap-3">
                                <Search
                                    searchQuery={searchQuery}
                                    handleSearchChange={handleSearchChange}
                                    setSearchQuery={updateSearch}
                                />
                                <div style={{ minWidth: 240 }}>
                                    <Select
                                        value={district}
                                        onChange={(option) => handleDistrictChange(option as Option | null)}
                                        options={districtOptions}
                                        isSearchable={true}
                                        isClearable={true}
                                        placeholder="All Districts"
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                </div>
                                <div style={{ minWidth: 160 }}>
                                    <Select
                                        value={siteType}
                                        onChange={(option) => handleSiteTypeChange(option as Option | null)}
                                        options={SITE_TYPE_OPTIONS}
                                        isSearchable={false}
                                        isClearable={true}
                                        placeholder="All Types"
                                        menuPortalTarget={document.body}
                                        styles={selectStyles}
                                    />
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <input
                                        type="date"
                                        className="deviceLogsDate"
                                        aria-label="From date"
                                        value={fromDate}
                                        max={toDate}
                                        onChange={handleFromDateChange}
                                    />
                                    <span className="deviceLogsDateSeparator">to</span>
                                    <input
                                        type="date"
                                        className="deviceLogsDate"
                                        aria-label="To date"
                                        value={toDate}
                                        min={fromDate}
                                        max={today}
                                        onChange={handleToDateChange}
                                    />
                                </div>
                            </div>
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                totalPages={totalPages}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="paginationDiv h-100 pt-3 mb-lg-0 mb-md-3 mb-3">
                <div className="row g-4 flex-lg-column flex-md-column flex-column-reverse h-100">
                    <div className="col-12 h-100">
                        <div className="pageTable h-100">
                            <div className="pageTableInner personalTab">
                                {/* Always show the table so the header stays; the message goes in the body */}
                                <Table
                                    data={error ? [] : rows}
                                    columns={columns}
                                    emptyMessage={isLoading ? 'Loading...' : error || 'No device logs found.'}
                                />
                            </div>

                            <MobilePagination
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                totalPages={totalPages}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {selectedLog && (
                <DeviceLoginHistoryModal
                    log={selectedLog}
                    fromDate={fromDate}
                    toDate={toDate}
                    onClose={closeHistory}
                />
            )}
        </>
    );
};

export default DeviceLogs;
